import { DocumentTextIcon, PlusIcon, TrashIcon } from '@heroicons/react/24/outline'
import { AiArtifact, createAiProjectArtifact, deleteAiProjectArtifact } from 'services'
import { Button } from 'stories/components'
import { formatTime } from 'utils'

interface ArtifactsPanelProps {
  projectId: number
  /** Set inside a loan: what gets created here is filed under that loan. */
  loanNumber?: number
  artifacts: AiArtifact[]
  onChanged: () => void
  onOpen: (artifactId: number) => void
}

/**
 * Documents saved in the project. Most arrive from the assistant through create_artifact; a blank
 * one can also be started here and handed to the assistant to fill in.
 *
 * Inside a loan the list mixes that loan's artifacts with the project's unscoped ones (templates
 * and standing documents), so each row says which it is.
 */
export function ArtifactsPanel({ projectId, loanNumber, artifacts, onChanged, onOpen }: ArtifactsPanelProps) {
  const onCreate = async () => {
    const artifact = await createAiProjectArtifact(projectId, {
      title: 'Untitled artifact',
      kind: 'markdown',
      content: '',
      loanNumber,
    })
    onChanged()
    onOpen(artifact.id)
  }

  const onDelete = async (artifact: AiArtifact) => {
    if (!window.confirm(`Delete "${artifact.title}"?`)) return
    await deleteAiProjectArtifact(projectId, artifact.id)
    onChanged()
  }

  return (
    <div className="flex flex-col h-full min-h-0">
      <div className="px-4 py-3 border-b border-gray-200">
        <Button size="sm" onClick={onCreate}>
          <span className="flex items-center gap-1.5">
            <PlusIcon className="w-4 h-4" />
            New artifact
          </span>
        </Button>
      </div>

      <div className="flex-1 overflow-y-auto p-3 space-y-2">
        {!artifacts.length && (
          <p className="text-sm text-gray-500 p-3">
            No artifacts yet. Ask the assistant for a report, memo, template, or query and it will save the result
            here so you can reopen and revise it.
          </p>
        )}
        {artifacts.map((artifact) => (
          <div
            key={artifact.id}
            className="border border-gray-200 rounded-md p-2.5 bg-white hover:border-shade-blue transition-colors"
          >
            <div className="flex items-start justify-between gap-2">
              <button className="min-w-0 text-left flex-1" onClick={() => onOpen(artifact.id)}>
                <p className="text-sm font-medium text-gray-800 truncate flex items-center gap-1.5">
                  <DocumentTextIcon className="w-4 h-4 shrink-0 text-gray-400" />
                  {artifact.title}
                </p>
                <p className="text-xs text-gray-500 mt-0.5">
                  {artifact.kind}
                  {artifact.language ? `/${artifact.language}` : ''} · v{artifact.version} ·{' '}
                  {formatTime(artifact.updatedAt)}
                </p>
                {!!loanNumber && (
                  <span
                    className={`inline-block mt-1 rounded px-1.5 py-0.5 text-[11px] font-medium ${
                      artifact.loanNumber
                        ? 'bg-shade-blue/10 text-shade-blue'
                        : 'bg-gray-100 text-gray-500'
                    }`}
                  >
                    {artifact.loanNumber ? `Loan ${artifact.loanNumber}` : 'Project-wide'}
                  </span>
                )}
              </button>
              <button
                className="p-1 text-gray-400 hover:text-red-600 shrink-0"
                title="Delete"
                onClick={() => onDelete(artifact)}
              >
                <TrashIcon className="w-4 h-4" />
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}
